
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  maxTocLevel: 2,
  description: 'Learn how to troubleshoot common Transactions issues including API error responses and incorrect data fields.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Checkbox = makeShortcode("Checkbox");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Troubleshooting Transactions' subtitle={metadata.description} mdxType="Header" />
    <h4 {...{
      "id": "api-error-responses"
    }}>{`API error responses`}</h4>
    <p>{`If the problem you're encountering involves an API error message, see `}<a parentName="p" {...{
        "href": "/docs/errors"
      }}>{`Errors`}</a>{` for troubleshooting suggestions.`}</p>
    <h4 {...{
      "id": "no-transactions-or-cursor-returned"
    }}>{`No transactions or cursor returned`}</h4>
    <p>{`When calling `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{`, a response with no cursor or transactions data is returned.`}</p>
    <h5 {...{
      "id": "common-causes"
    }}>{`Common causes`}</h5>
    <ul>
      <li parentName="ul">{`This is expected behavior when the Item has recently been initialized with `}<inlineCode parentName="li">{`transactions`}</inlineCode>{` and Plaid has not yet completed fetching of transactions data.`}</li>
    </ul>
    <h5 {...{
      "id": "troubleshooting-steps"
    }}>{`Troubleshooting steps`}</h5>
    <Checkbox mdxType="Checkbox">
      <p>{`If you have already called `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` on the Item, listen for the `}<inlineCode parentName="p">{`SYNC_UPDATES_AVAILABLE`}</inlineCode>{` webhook and call `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` again after it has been received. Note that you will need to call `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` at least once on an Item to activate the `}<inlineCode parentName="p">{`SYNC_UPDATES_AVAILABLE`}</inlineCode>{` webhook.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`If you have not already called `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` on the Item, listen for the `}<inlineCode parentName="p">{`INITIAL_UPDATE`}</inlineCode>{` webhook and call `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` again after it has been received.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Wait a few seconds and try the `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` call again.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "missing-transactions"
    }}>{`Missing transactions`}</h4>
    <h5 {...{
      "id": "common-causes-1"
    }}>{`Common causes`}</h5>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`The institution is experiencing an error.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Processing of the transactions has been delayed.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The Item is not in a healthy state.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The transaction is not available on the institution's online banking portal, or appears differently on the online banking portal.`}</p>
      </li>
    </ul>
    <h5 {...{
      "id": "troubleshooting-steps-1"
    }}>{`Troubleshooting steps`}</h5>
    <Checkbox mdxType="Checkbox">
      <p>{`Check the `}<a parentName="p" {...{
          "href": "https://status.plaid.com"
        }}>{`Plaid status page`}</a>{` for any known system-wide outages.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Check institution insights via the `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/activity/status"
        }}>{`Plaid Dashboard`}</a>{` or the `}<inlineCode parentName="p">{`/institutions/get_by_id`}</inlineCode>{` endpoint. A degraded status indicates that the issue is known to Plaid and under investigation.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Ensure that the Item is not in an error state by examining the error returned by the failing endpoint, or making an `}<inlineCode parentName="p">{`/item/get`}</inlineCode>{` request to get more information. If the Item is in an error state, see the `}<a parentName="p" {...{
          "href": "/docs/errors"
        }}>{`error guide`}</a>{` for the specific error that was encountered.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Ask the user to ensure that the given transaction is visible in the institution's online banking portal. Other sources, such as paper statements, should not be used as the source of truth to compare to Plaid data. Also verify that the transaction is not being reported under a different description. Plaid cleans and standardizes transaction data, so it is possible for a transaction to be returned that does not have the exact description provided by the bank.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Wait two business days and try calling `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` or `}<inlineCode parentName="p">{`/transactions/get`}</inlineCode>{` again.`}</p>
    </Checkbox>
    <p>{`If the steps above do not resolve the issue, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/support/new/financial-institutions/missing-data/missing-transactions"
      }}>{`file a ticket with Plaid support`}</a>{`.`}</p>
    <h4 {...{
      "id": "no-credit-card-accounts-returned"
    }}>{`No credit card accounts returned`}</h4>
    <h5 {...{
      "id": "common-causes-2"
    }}>{`Common causes`}</h5>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`auth`}</inlineCode>{` was initialized in the `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{` call `}<inlineCode parentName="li">{`products`}</inlineCode>{` array, along with `}<inlineCode parentName="li">{`transactions`}</inlineCode>{`. Auth and Transfer products do not support credit cards, so including them will exclude credit card accounts.`}</li>
    </ul>
    <h5 {...{
      "id": "troubleshooting-steps-2"
    }}>{`Troubleshooting steps`}</h5>
    <ul>
      <li parentName="ul">{`Remove any products that do not support credit card accounts, such as `}<inlineCode parentName="li">{`auth`}</inlineCode>{`, `}<inlineCode parentName="li">{`transfer`}</inlineCode>{`, and `}<inlineCode parentName="li">{`signal`}</inlineCode>{`, from the `}<inlineCode parentName="li">{`products`}</inlineCode>{` array. If these products may be used for optional flows in your app (e.g. you use ACH from a customer's linked account as a subscription payment method), put them in the `}<inlineCode parentName="li">{`optional_products`}</inlineCode>{` array instead.`}</li>
    </ul>
    <h4 {...{
      "id": "incorrect-data-fields"
    }}>{`Incorrect data fields`}</h4>
    <p>{`In some cases, information about a merchant, such as its name or category, may be incorrect.`}</p>
    <h5 {...{
      "id": "troubleshooting-steps-3"
    }}>{`Troubleshooting steps`}</h5>
    <Checkbox mdxType="Checkbox">
      <p>{`If the `}<inlineCode parentName="p">{`name`}</inlineCode>{` field is incorrect, try the `}<inlineCode parentName="p">{`merchant_name`}</inlineCode>{` field, or vice-versa. The `}<inlineCode parentName="p">{`merchant_name`}</inlineCode>{` field has a higher level of processing applied to the raw transaction data and may be more likely to match your users' expectations of transaction data.`}</p>
    </Checkbox>
    <p>{`If the issue is not resolved, or if the category information is incorrect, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/support/new/financial-institutions/faulty-data/faulty-transaction-data"
      }}>{`file a ticket with Plaid support`}</a>{` indicating which fields are incorrect, the correct data, and the `}<inlineCode parentName="p">{`transaction_id`}</inlineCode>{` of the affected transaction.`}</p>
    <h4 {...{
      "id": "limited-transaction-history-is-returned"
    }}>{`Limited transaction history is returned`}</h4>
    <h5 {...{
      "id": "common-causes-3"
    }}>{`Common causes`}</h5>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`By default, Plaid will only request 90 days of transaction history from the institution when Transactions is first added to an Item; history then accumulates over the life of the Item (see `}<a parentName="p" {...{
            "href": "/docs/transactions/#how-much-transaction-history-is-available"
          }}>{`How much transaction history is available`}</a>{`). To request more initial history, specify the `}<inlineCode parentName="p">{`days_requested`}</inlineCode>{` field when initializing with Transactions. Note that this field will only take effect when Transactions is added to an Item for the first time; if you need more than 90 days of transactions history for an Item that has already been initialized with Transactions for 90 days of history, you will need to delete the Item and create a new one.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The user's institution may provide limited transaction history. For example, Capital One provides only 90 days of transaction history and does not provide pending transactions.`}</p>
      </li>
    </ul>
    <h5 {...{
      "id": "troubleshooting-steps-4"
    }}>{`Troubleshooting steps`}</h5>
    <Checkbox mdxType="Checkbox">
      <p>{`Specify `}<inlineCode parentName="p">{`days_requested`}</inlineCode>{` when calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` or (if not initializing with Transactions during Link) when calling `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` or `}<inlineCode parentName="p">{`/transactions/get`}</inlineCode>{` for the first time on an Item.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Verify that the issue is not specific to a particular institution. For details of history available at certain major institutions, see the `}<a parentName="p" {...{
          "href": "https://plaid.com/documents/oauth-institution-ux.pdf"
        }}>{`Plaid guide to institution-specific OAuth experiences`}</a>{`.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "duplicate-transactions-are-returned"
    }}>{`Duplicate transactions are returned`}</h4>
    <h5 {...{
      "id": "common-causes-4"
    }}>{`Common causes`}</h5>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`An end user linked the same bank account to your app multiple times, causing the same transaction data to appear across multiple Items.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The duplicate transaction data may be an accurate reflection of the end user's transaction activity (e.g. if the end user was double-charged).`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`If a transaction is updated, modified, or posted, it will appear as a separate transaction in transaction history. This is expected behavior and your app should have logic to reconcile the two versions of the transaction. For more details, see `}<a parentName="p" {...{
            "href": "/docs/transactions/transactions-data/"
          }}>{`Transactions states`}</a>{`.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`There may be an error with the data provided by the institution to Plaid or with Plaid's processing of this data.`}</p>
      </li>
    </ul>
    <h5 {...{
      "id": "troubleshooting-steps-5"
    }}>{`Troubleshooting steps`}</h5>
    <Checkbox mdxType="Checkbox">
      <p>{`Confirm that the transaction and duplicate transaction are associated with the same Plaid `}<inlineCode parentName="p">{`account_id`}</inlineCode>{`. If the account IDs are different, your user likely linked the same account multiple times, causing data for that account to be duplicated in your app. For details on preventing and managing this situation, see `}<a parentName="p" {...{
          "href": "/docs/link/duplicate-items"
        }}>{`Duplicate Items`}</a>{`.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Confirm with the end user that the transaction and duplicate transaction do not both appear in their online banking activity.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Confirm that the transaction and duplicate transaction are not pending and posted versions of the same transaction. A posted transaction will have a `}<inlineCode parentName="p">{`pending_transaction_id`}</inlineCode>{` field linking it to the pending version of the transaction.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Confirm that the transaction is not a `}<a parentName="p" {...{
          "href": "/docs/api/products/transactions/#transactions-sync-response-modified"
        }}>{`modified`}</a>{` version of an existing transaction. When using `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{`, a modified transaction will appear within the `}<inlineCode parentName="p">{`modified`}</inlineCode>{` array.`}</p>
    </Checkbox>
    <p>{`If the issue is not resolved, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/support/new/financial-institutions/faulty-data/faulty-transaction-data"
      }}>{`file a ticket with Plaid support`}</a>{` indicating which transactions are duplicated, and the `}<inlineCode parentName="p">{`transaction_id`}</inlineCode>{`s of the affected transaction.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "api-error-responses",
        level: 4,
        title: "API error responses",
      },
{
        id: "no-transactions-or-cursor-returned",
        level: 4,
        title: "No transactions or cursor returned",
      },
{
        id: "missing-transactions",
        level: 4,
        title: "Missing transactions",
      },
{
        id: "no-credit-card-accounts-returned",
        level: 4,
        title: "No credit card accounts returned",
      },
{
        id: "incorrect-data-fields",
        level: 4,
        title: "Incorrect data fields",
      },
{
        id: "limited-transaction-history-is-returned",
        level: 4,
        title: "Limited transaction history is returned",
      },
{
        id: "duplicate-transactions-are-returned",
        level: 4,
        title: "Duplicate transactions are returned",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
